/* Relatórios dos agentes: o texto mais recente de cada agente, por usuário.
 *
 * A tabela `ainvestor_agents_output` guarda todas as rodadas; o backend já entrega
 * só a última por (usuário, agente), então esta tela não filtra nada — apenas
 * escolhe o usuário e decide COMO ler: os dois agentes lado a lado, para comparar,
 * ou um por vez, para leitura corrida. O markdown vem cru do banco e passa pelo
 * <Markdown> de ui.tsx, compartilhado com o Margin Finder.
 */

import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { AgentReport, ReportsLatestPayload } from "../lib/api";
import { compact, fmtDateTime, shortId } from "../lib/format";
import { Card, Empty, ErrorState, Markdown, PageHead, Skeleton } from "../components/ui";
import { agentLabel } from "../lib/agentes";
import { AgentsRefresh } from "../components/AgentsRefresh";
import { useUsers } from "../components/Shell";

const PROFILE_AGENT = "agent_profile";

/* O mapa de rótulos mora em `lib/agentes.ts`: o Margin Finder também é dirigido pelos
 * agentes descobertos, e dois mapas separados divergiriam no primeiro agente novo. Um
 * id sem entrada deixou de aparecer cru — vira "Custo financeiro" e afins. */

function ReportCard({ agentId, report }: { agentId: string; report: AgentReport | null }) {
  return (
    <Card title={agentLabel(agentId)}
          sub={report ? `gerado em ${fmtDateTime(report.created_at)}` : undefined}>
      {report
        ? <Markdown text={report.user_report} />
        : <Empty>Sem relatório deste agente para este usuário.</Empty>}
    </Card>
  );
}

export function Reports() {
  const [params, setParams] = useSearchParams();
  const { data: users } = useUsers();

  const reports = useQuery({
    queryKey: ["reports-latest"],
    queryFn: () => api<ReportsLatestPayload>("/api/reports/latest"),
    // O servidor guarda um cache de 12 h; revalidar por tempo aqui só o rebaixaria.
    // Dado novo chega por UMA porta: o botão "Atualizar dados" invalida a query.
    staleTime: Infinity,
    gcTime: 12 * 60 * 60_000,
    retry: false,
    placeholderData: (previous) => previous,
  });
  const data = reports.data;

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  /* O servidor ordena por `user_id`; o <select> ordena pelo perfil mais recente, para
   * que quem acabou de chegar apareça primeiro. Sem perfil vai para o fim. */
  const profileTime = (u: ReportsLatestPayload["users"][number]) => {
    const at = u.agents[PROFILE_AGENT]?.created_at;
    return at ? Date.parse(at) : 0;
  };
  const ordered = [...(data?.users ?? [])].sort((a, b) => profileTime(b) - profileTime(a));

  const userId = params.get("usuario") || ordered[0]?.user_id || "";
  const mode = params.get("modo") === "um" ? "um" : "lado";
  const agentId = params.get("agente") || data?.agent_ids[0] || "";

  /** Rótulo do <select>: a data do perfil (a chave da ordem) e, quando o painel
   *  conhece o id, meses e massa. */
  const optionLabel = (u: ReportsLatestPayload["users"][number]) => {
    const at = u.agents[PROFILE_AGENT]?.created_at;
    const head = `${shortId(u.user_id)}…  ·  ${at ? `perfil ${fmtDateTime(at)}` : "sem perfil"}`;
    const known = users?.find((p) => !p.is_aggregate && p.user_id === u.user_id);
    return known
      ? `${head}  ·  ${known.months} meses  ·  R$ ${compact(known.total)}`
      : head;
  };

  const current = data?.users.find((u) => u.user_id === userId);

  return (
    <>
      <PageHead eyebrow="agentes" title="Relatórios">
        O relatório mais recente de cada agente. Escolha o usuário e compare os
        agentes lado a lado, ou leia um por vez. Os dados são relidos do Supabase
        no máximo a cada 12 h — use “Atualizar dados” para reler agora.
      </PageHead>

      {data && (
        <div className="controls">
          <label>usuário
            <select value={userId} onChange={(e) => set("usuario", e.target.value)}>
              {ordered.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {optionLabel(u)}
                </option>
              ))}
            </select>
          </label>

          <div className="chips" role="group" aria-label="Modo de leitura">
            <button className="chip" aria-pressed={mode === "lado"}
                    onClick={() => set("modo", "")}>
              lado a lado
            </button>
            <button className="chip" aria-pressed={mode === "um"}
                    onClick={() => set("modo", "um")}>
              um por vez
            </button>
          </div>

          {mode === "um" && (
            <div className="chips" role="group" aria-label="Agente">
              {data.agent_ids.map((id) => (
                <button key={id} className="chip" aria-pressed={id === agentId}
                        onClick={() => set("agente", id)}>
                  {agentLabel(id)}
                </button>
              ))}
            </div>
          )}

          <AgentsRefresh fetchedAt={data.fetched_at} stale={data.stale} />
        </div>
      )}

      {reports.isPending && <Skeleton rows={10} />}
      {reports.error && (
        <ErrorState error={reports.error} onRetry={() => reports.refetch()} />
      )}

      {data && !current && (
        <Empty onClear={() => set("usuario", "")}>
          Usuário sem relatórios.
        </Empty>
      )}

      {data && current && (
        mode === "lado" ? (
          <div style={{ marginTop: 18, display: "grid", gap: 18,
                        gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))" }}>
            {data.agent_ids.map((id) => (
              <ReportCard key={id} agentId={id} report={current.agents[id] ?? null} />
            ))}
          </div>
        ) : (
          <div style={{ marginTop: 18 }}>
            <ReportCard agentId={agentId} report={current.agents[agentId] ?? null} />
          </div>
        )
      )}
    </>
  );
}
